import {
	Breakpoints,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Divider,
	ExternalLinkIndicator,
	Gallery,
	GallerySlide,
	DecorativeImage,
	Layout,
	NotificationBar,
	Spinner,
	Text,
	TextTag,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { Info } from '@volkswagen-onehub/icons-core';
import { LayerCloseHandleV2, LayerHandleV2, LayerManagerV24, LayerStyleOptionsV2 } from '@volkswagen-onehub/layer-manager';
import { ContentLayerV2OptionsV2 } from '@volkswagen-onehub/layer-manager/dist/v2/types';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { HistoryServiceV3 } from '@feature-hub/history-service';
import { IAuthServiceV2 } from '@volkswagen-onehub/authservice';
import { UserInteractionServiceV22 } from '@volkswagen-onehub/user-interaction-service';
import { ScopedDisclaimerManager } from '@volkswagen-onehub/disclaimer-manager';
import EfficiencyDetails from '../../components/Efficiency/EfficiencyDetails';
import EngineDetails from '../../components/Engine/EngineDetails';
import renderShareVehicle from '../../components/ShareVehicle/ShareVehicle';
import SpecialEquipment from '../../components/SpecialEquipment/SpecialEquipment';
import TechnicalDetails, { TechnicalDetailsProps } from '../../components/TechnicalDetails/TechnicalDetails';
import renderVehicleVerification from '../../components/VehicleVerification/VehicleVerification';
import { Vehicle, VehicleObject } from '../../models/Vehicle';
import RecallLayer, { RecallLayerProps } from '../../components/RecallLayer/RecallLayer';
import './VinDetails.scss';
import UserVerification, { UserVerificationProps } from '../../components/UserVerification/UserVerification';
import VehicleVerificationFeedback, { VehicleVerificationFeedbackProps } from '../../components/VehicleVerificitationFeedback/VehicleVerificationFeedback';
import { openContentLayer } from '../../service/LayerService/LayerService';
import { getVehicleDetails } from '../../service/VinInputService/VinInputService';
import { FeatureServicesContext } from '../../use-feature-services';
import { getAdditionalVehicleDetails, getRecalls } from '../../service/AdditionalVehicleDataService/VinInputService';
import { Recalls } from '../../models/Recalls';
import { DataRequestState } from '../../models/DataRequestState';
import { AuthStatus } from '../../models/AuthStatus';
import { AddBreadcrumb } from '../../use-breadcrumb';
import StandardEquipment from '../../components/StandardEquipment/StandardEquipment';
import { getShareUrl, getShareVehicleData } from '../../service/ShareVehicleService/SharedVehicleService';
import { TechnicalData, TechnicalDataObject } from '../../models/TechnicalData';
import VehicleNotFoundModal from '../../components/VehicleNotFoundModal/VehicleNotFoundModal';
import { LanguageCountryCode } from '../../context/language-country-code';
import Disclaimer from '../../atoms/Disclaimer/Disclaimer';
import { NavigationImages } from '../../organisms/NavigationImages/navigation-images';
import { useAwsWaf } from '../../hooks/useAwsWaf';

function VinDetails({ authService, authServiceMarket }: Props) {
	const navigate = useNavigate();
	const location = useLocation();
	const { t, i18n } = useTranslation('ns1', { useSuspense: false });
	const { token } = useParams();
	let vin: string | null = (location.state as { vin: string })?.vin;
	if (!vin) {
		vin = window.sessionStorage.getItem('vin');
	} else {
		window.sessionStorage.setItem('vin', vin);
	}

	// If no vin (through user input or session storage) or token (share url) is set, redirect to start page.
	if (!vin && !token) {
		navigate('/', { state: { brokenLink: true }, replace: true });
	}

	const featureServices = useContext(FeatureServicesContext);
	const language = useContext(LanguageCountryCode);
	let baseUrl = '';
	let scopedDisclaimerManager = {} as ScopedDisclaimerManager;
	let userInteractionService: UserInteractionServiceV22 = {} as UserInteractionServiceV22;
	let historyService: HistoryServiceV3 = {} as HistoryServiceV3;

	const layerStyleOptions = {} as LayerStyleOptionsV2;
	const layerManager = useMemo(
		() => (featureServices && featureServices['layer-manager'] ? featureServices['layer-manager'] : ({} as LayerManagerV24)),
		[featureServices],
	);

	if (featureServices && featureServices['fa-content-service']) {
		baseUrl = featureServices['fa-content-service']!.content.baseAssetUrl;
		const disclaimerManager = featureServices['disclaimer-manager'];
		scopedDisclaimerManager = disclaimerManager.getScopedDisclaimerManager();
		userInteractionService = featureServices['user-interaction-service'];
		historyService = featureServices['s2:history'] as HistoryServiceV3;
	}

	const wafChallengeScriptUrl = featureServices?.['fa-content-service']?.content.wafChallengeScriptUrl;
	const wafCookieDomainList = useMemo(
		() =>
			(featureServices?.['fa-content-service']?.content.wafCookieDomainList ?? '')
				.split(',')
				.map((domain) => domain.trim())
				.filter(Boolean),
		[featureServices],
	);
	const isWafReady = useAwsWaf({ challengeScriptUrl: wafChallengeScriptUrl, cookieDomainList: wafCookieDomainList });
	let trackingService: any;
	if (featureServices && featureServices.tracking) {
		trackingService = featureServices.tracking;
	}

	const footnotes = [
		{ reference: '', text: 'emission', handle: {} as LayerHandleV2<any> },
		{ reference: '', text: 'electricRange', handle: {} as LayerHandleV2<any> },
		{ reference: '', text: 'equipment', handle: {} as LayerHandleV2<any> },
	];

	const renderDisclaimer = (state: any, close: LayerCloseHandleV2<any>) => <Disclaimer reference={state.reference} text={state.text} close={close} />;

	const onClickDisclaimer = (disclaimerNumber: number) => {
		if (footnotes[disclaimerNumber].handle.isClosed && !footnotes[disclaimerNumber].handle.isClosed()) {
			footnotes[disclaimerNumber].handle.close();
			return;
		}

		footnotes[disclaimerNumber].handle = layerManager.openDisclaimerLayer(renderDisclaimer, {
			reference: footnotes[disclaimerNumber].reference,
			text: i18n.t(`footnotes.${footnotes[disclaimerNumber].text}` as any),
			close: layerStyleOptions.closeButton,
		});
	};

	if (scopedDisclaimerManager && scopedDisclaimerManager.registerReferenceDisclaimer) {
		footnotes.forEach((footnote, index) => {
			footnotes[index].reference = scopedDisclaimerManager.registerReferenceDisclaimer('module-reference', footnote.text, undefined, 'VNC');
		});
	}

	const [vehicleObject, setVehicleObject] = useState({ data: {} as Vehicle, state: DataRequestState.None } as VehicleObject);
	const [technicalDataObject, setTechnicalDataObject] = useState({ data: {} as TechnicalData, state: DataRequestState.None } as TechnicalDataObject);
	const [vehicleDataIncomplete, setVehicleDataIncomplete] = useState(false);
	const [vehicleDataIncompleteLayerVisible, setVehicleDataIncompleteLayerVisible] = useState(false);

	const [vinToken, setVinToken] = useState({
		data: { loginStatusChanged: false, isAuthenticated: false, vinToken: '' },
		state: DataRequestState.None,
	} as AuthStatus);
	const [recalls, setRecalls] = useState({ state: DataRequestState.None } as Recalls);
	// TODO 21122021 still required?
	const [, setTechnicalDetailsHandle] = useState({} as LayerHandleV2<any>);
	const [, setVehicleVerificationHandle] = useState({} as LayerHandleV2<any>);
	const [isVerificationLayerActive, setIsVerificationLayerActive] = useState(false);
	const [isNotFoundLayerActive, setNotFoundLayerActive] = useState(true);
	const [errorVinNotFound, setErrorVinNotFound] = useState(false);
	const [appConnectNotificationVisible, setAppConnectNotificationVisible] = useState(false);

	const getFuelConsumption = (): string => {
		if (technicalDataObject.data.consumptionNEDC?.consumptionUrban && technicalDataObject.data.consumptionNEDC.consumptionOutOfTown) {
			return `${technicalDataObject.data.consumptionNEDC.consumptionUrban} - ${technicalDataObject.data.consumptionNEDC.consumptionOutOfTown}`;
		}

		if (technicalDataObject.data.consumptionNEDC?.consumptionCombined) {
			return technicalDataObject.data.consumptionNEDC.consumptionCombined;
		}

		if (technicalDataObject.data.consumptionNEDC?.consumptionWightedCombined) {
			return technicalDataObject.data.consumptionNEDC.consumptionWightedCombined;
		}

		return '-';
	};

	const getEnergyConsumption = (): string => {
		if (technicalDataObject.data.consumptionNEDC?.consumptionEnergyCombined) {
			return technicalDataObject.data.consumptionNEDC.consumptionEnergyCombined;
		}

		return '-';
	};

	const getConsumption = (): string => {
		if (!technicalDataObject.data?.consumptionNEDC) {
			return '-';
		}

		if (
			// TODO 15082022 do in backend
			technicalDataObject.data.engineGearboxElectrics?.fuelType === 'Elektro' ||
			technicalDataObject.data.engineGearboxElectrics?.fuelType === 'Electric' ||
			technicalDataObject.data.engineGearboxElectrics?.fuelType === 'Napęd elektryczny'
		) {
			return getEnergyConsumption();
		}

		return getFuelConsumption();
	};

	const getElectricalRange = (): string => {
		if (technicalDataObject.data.consumptionNEDC?.rangeElectric) {
			return `${technicalDataObject.data.consumptionNEDC.rangeElectric} (NEDC)`;
		}

		if (technicalDataObject.data.consumptionWLTP?.rangeElectricOutOfTown && technicalDataObject.data.consumptionWLTP?.rangeElectricUrban) {
			return `${technicalDataObject.data.consumptionWLTP.rangeElectricOutOfTown} - ${technicalDataObject.data.consumptionWLTP.rangeElectricUrban} (WLTP)`;
		}

		return '-';
	};

	const getEmission = (): string => {
		if (!technicalDataObject.data?.consumptionNEDC) {
			return '-';
		}

		if (technicalDataObject.data.consumptionNEDC.co2Combined) {
			return technicalDataObject.data.consumptionNEDC.co2Combined;
		}

		if (technicalDataObject.data.consumptionNEDC.co2WeightedCombined) {
			return technicalDataObject.data.consumptionNEDC.co2WeightedCombined;
		}

		return '-';
	};

	const getEmissionOrRange = (): string => {
		if (
			// TODO 15082022 do in backend
			technicalDataObject.data.engineGearboxElectrics?.fuelType === 'Elektro' ||
			technicalDataObject.data.engineGearboxElectrics?.fuelType === 'Electric' ||
			technicalDataObject.data.engineGearboxElectrics?.fuelType === 'Napęd elektryczny'
		) {
			return getElectricalRange();
		}

		return getEmission();
	};

	const getShareUrlConst = async (withAdditionalContent: boolean) => {
		if (!featureServices) {
			return '';
		}

		return getShareUrl(
			authService,
			`${featureServices['fa-content-service']?.content.resourceHostId}`,
			vin!,
			vehicleObject.data.productBrand,
			withAdditionalContent,
			language,
			`${featureServices['fa-content-service']?.content.BID}`,
			`${featureServices['fa-content-service']?.content.countryCode}`,
			`${featureServices['fa-content-service']?.content.baseApiUrl}`,
			`${featureServices['fa-content-service']?.content.apiKey}`,
			withAdditionalContent ? vinToken.data.vinToken : '',
		);
	};

	const vinCheckTrackingLayerClose = (state: any) => {
		trackingService.track('VWBasic_LayerClose_Click', {
			eventInfo: {
				eventAction: 'VWBasic_LayerClose_Click',
				pageId: window.location.href,
				pageName: 'Vin Details',
				contentId: state.tracking.contentId,
				sectionName: state.tracking.sectionName,
				brand: 'passenger',
			},
			user: {
				browserResolutionHeight: window.innerHeight,
				browserResolutionWidth: window.innerWidth,
				browserResolutionOrientation: window.innerHeight >= window.innerWidth ? 'v' : 'h',
			},
			personalization: {
				personalizable: false,
			},
			configuration: {
				CarlineName: vehicleObject.data.modelCarLine,
				SalesgroupName: vehicleObject.data.modelSalesGroup,
			},
		});
	};

	const toggleVehicleVerification = () => {
		if (!featureServices || !featureServices['fa-content-service']) {
			return;
		}

		layerManager.openContentLayerV2(
			renderVehicleVerification,
			{
				vin: vin!,
				preFilledAuthData: technicalDataObject.data.preFilledAuthData ?? { type: '??', variant: '??', version: '??' },
				language,
				authStatus: vinToken,
				setAuthStatus: setVinToken,
				baseApiUrl: featureServices['fa-content-service']!.content.baseApiUrl,
				baseAssetsUrl: featureServices['fa-content-service']!.content.baseAssetUrl,
				apiKey: featureServices['fa-content-service']!.content.apiKey,
				vehicle: vehicleObject,
				trackingService,
				isLoggedIn: vinToken.data.isAuthenticated,
				tracking: {
					contentId: 'vinDetails.vehicleVerification',
					sectionName: 'vehicleVerification',
				},
				t,
			},
			{
				closeButtonLabel: '',
				contentHeadline: '',
				userCloseable: false,
				// TODO 09122022 fix JavaScript magic
				onClose: vinCheckTrackingLayerClose,
			},
		);
	};

	const toggleUserVerification = () => {
		openContentLayer<UserVerificationProps>(
			layerManager,
			UserVerification,
			{
				getWeConnectUrl: () => {
					if (!featureServices) {
						return '';
					}

					return featureServices['fa-content-service']?.content.weConnectUrl ?? '';
				},
				signIn: () => {
					authService.login(undefined, `${window.location.href}`).finally();
				},
				trackingService,
				isLoggedIn: vinToken.data.isAuthenticated,
				vehicle: vehicleObject,
				tracking: {
					contentId: 'vinDetails.userVerification',
					sectionName: 'userVerification',
				},
				t,
			},
			{
				closeButtonLabel: '',
				contentHeadline: '',
				userCloseable: false,
				// TODO 09122022 fix JavaScript magic
				onClose: vinCheckTrackingLayerClose,
			},
		);
	};

	const getAppConnectUrl = () => {
		if (!featureServices || !featureServices['fa-content-service']) {
			return '';
		}

		// return featureServices['fa-content-service']!.content.appConnectUrl;
		return 'https://www.volkswagen.pl/pl/connected-car/app-connect.html';
	};

	const vinCheckTrackingAppConnectButtons = (trackingLocation: 'banner' | 'page') => {
		trackingService.track('VWBasic_Button_Click', {
			eventInfo: {
				eventAction: 'VWBasic_Vehicle_Load',
				pageId: window.location.href,
				pageName: 'Vin Details',
				contentId: `vinDetails.appConnect.${trackingLocation}`,
				sectionName: `appConnect.${trackingLocation}`,
				brand: 'passenger',
			},
			user: {
				browserResolutionHeight: window.innerHeight,
				browserResolutionWidth: window.innerWidth,
				browserResolutionOrientation: window.innerHeight >= window.innerWidth ? 'v' : 'h',
			},
			personalization: {
				personalizable: false,
			},
			configuration: {
				CarlineName: vehicleObject.data.modelCarLine,
				SalesgroupName: vehicleObject.data.modelSalesGroup,
			},
			link: {
				url: getAppConnectUrl(),
				name: 'AppConnect URL',
				newTab: true,
			},
		});
	};

	if (vin && !token && userInteractionService) {
		userInteractionService.register('toggle-share-layer', {
			action: () => {
				layerManager.openContentLayerV2(
					renderShareVehicle,
					{
						vin: vehicleObject.data.vin,
						historyService,
						getShareUrl: getShareUrlConst,
						authorized: !!vehicleObject.data.includesExtendedData,
						vehicle: vehicleObject,
						technicalData: technicalDataObject,
						trackingService,
						isLoggedIn: vinToken.data.isAuthenticated,
						tracking: {
							contentId: 'vinDetails.shareVehicle',
							sectionName: 'shareVehicle',
						},
						t,
					},
					{
						closeButtonLabel: '',
						contentHeadline: '',
						userCloseable: false,
						onClose: vinCheckTrackingLayerClose,
					},
				);
			},
		});
	}

	useEffect(() => {
		if (technicalDataObject.state === DataRequestState.Available && vin) {
			window.sessionStorage.setItem('technicalData', JSON.stringify(technicalDataObject.data));
		}
	}, [technicalDataObject.data, technicalDataObject.state, vin]);

	// Vehicle data
	useEffect(() => {
		if (!featureServices || vinToken.state !== DataRequestState.Available || !vin || errorVinNotFound || !isWafReady) {
			return;
		}

		const getTechnicalData = (technicalData: TechnicalData) => {
			if (window.sessionStorage.getItem('technicalData') && !vinToken.data.loginStatusChanged) {
				const loadedTechnicalData = JSON.parse(window.sessionStorage.getItem('technicalData')!);
				if (!loadedTechnicalData.preFilledAuthData) {
					setVehicleDataIncomplete(true);
					setVehicleDataIncompleteLayerVisible(true);
				}
				setTechnicalDataObject({ data: loadedTechnicalData, state: DataRequestState.Available });
				// toggleVehicleVerification();
				return;
			}

			setTechnicalDataObject((oldTechnicalData) => ({ ...oldTechnicalData, ...{ state: DataRequestState.Requested } }));

			getAdditionalVehicleDetails(
				authService,
				`${featureServices['fa-content-service']?.content.resourceHostId}`,
				vin,
				technicalData,
				language,
				`${featureServices['fa-content-service']?.content.baseApiUrl}`,
				`${featureServices['fa-content-service']?.content.apiKey}`,
				vinToken.data.vinToken,
			).then((res) => {
				if (res.furtherInformation) {
					// Our first /vehicleData request does not return the delivery country. This data is contained in the /additionalVehicleData
					// request. Thus, we need to cut it out of the technicalData.furtherInformation and save it to vehicleDetails.
					// This step is necessary for the technical data layer.
					setVehicleObject((oldVehicle) => {
						// Deep copy
						const updatedVehicle: VehicleObject = JSON.parse(JSON.stringify(oldVehicle));
						updatedVehicle.data.vehicleDetails.deliveryCountryName = res.furtherInformation?.deliveryCountryName ?? 'Germany';
						updatedVehicle.data.vehicleDetails.deliveryCountryNameEnglish = res.furtherInformation?.deliveryCountryNameEnglish ?? 'Germany';
						return updatedVehicle;
					});
				}

				if (!res.preFilledAuthData) {
					setVehicleDataIncomplete(true);
					setVehicleDataIncompleteLayerVisible(true);
				}
				setTechnicalDataObject({ data: res, state: DataRequestState.Available });
			});
		};

		const vinCheckTrackingAppConnect = (appConnectStatus: string) => {
			trackingService.track('VWBasic_Vehicle_Load', {
				eventInfo: {
					eventAction: 'VWBasic_Vehicle_Load',
					pageId: window.location.href,
					pageName: 'Vin Details',
					contentId: `vinDetails.appConnect`,
					sectionName: `appConnect`,
					brand: 'passenger',
				},
				user: {
					browserResolutionHeight: window.innerHeight,
					browserResolutionWidth: window.innerWidth,
					browserResolutionOrientation: window.innerHeight >= window.innerWidth ? 'v' : 'h',
				},
				personalization: {
					personalizable: false,
				},
				configuration: {
					CarlineId: vin,
					EquipmentlineName: appConnectStatus,
					CarlineName: vehicleObject.data.modelCarLine,
					SalesgroupName: vehicleObject.data.modelSalesGroup,
				},
			});
		};

		const getVehicle = () => {
			if (window.sessionStorage.getItem('vehicle') && !vinToken.data.loginStatusChanged) {
				const loadedVehicle: Vehicle = JSON.parse(window.sessionStorage.getItem('vehicle')!);
				setVehicleObject({ data: loadedVehicle, state: DataRequestState.Available } as VehicleObject);
				setAppConnectNotificationVisible(loadedVehicle.appConnectStatus === 'locked');
				setTechnicalDataObject((oldTechnicalData) => ({ ...oldTechnicalData, ...{ state: DataRequestState.Requested } }));

				getTechnicalData(loadedVehicle.technicalDetails!);
				return;
			}

			setVehicleObject((oldVehicle) => ({ ...oldVehicle, ...{ state: DataRequestState.Requested } }));

			getVehicleDetails(
				authService,
				`${featureServices['fa-content-service']?.content.resourceHostId}`,
				vin,
				language,
				`${featureServices['fa-content-service']?.content.baseApiUrl}`,
				`${featureServices['fa-content-service']?.content.BID}`,
				`${featureServices['fa-content-service']?.content.countryCode}`,
				`${featureServices['fa-content-service']?.content.apiKey}`,
				vinToken.data.vinToken,
			)
				.then((res) => {
					const vehicleResponse = res;
					const technicalDataResponse = res.technicalDetails;
					delete vehicleResponse.technicalDetails;

					if (vehicleResponse.includesTechnicalDetails) {
						setTechnicalDataObject({ data: technicalDataResponse!, state: DataRequestState.Available });
						if (!technicalDataResponse!.preFilledAuthData) {
							setVehicleDataIncomplete(true);
							setVehicleDataIncompleteLayerVisible(true);
						}
					} else {
						getTechnicalData(technicalDataResponse!);
					}

					setAppConnectNotificationVisible(res.appConnectStatus === 'locked');
					setVehicleObject({ data: vehicleResponse, state: DataRequestState.Available });
					vinCheckTrackingAppConnect(res.appConnectStatus);
				})
				.catch(() => {
					setErrorVinNotFound(true);
					setNotFoundLayerActive(!isNotFoundLayerActive);
					setVehicleObject((oldVehicle) => ({ ...oldVehicle, ...{ state: DataRequestState.Failed } }));
				});
		};

		getVehicle();
	}, [
		authService,
		errorVinNotFound,
		featureServices,
		isNotFoundLayerActive,
		language,
		layerManager,
		trackingService,
		vehicleObject.data.modelCarLine,
		vehicleObject.data.modelSalesGroup,
		vin,
		vinToken,
		vinToken.data.loginStatusChanged,
		vinToken.data.vinToken,
		vinToken.state,
		isWafReady,
	]);

	useEffect(() => {
		if (vehicleObject.state === DataRequestState.Available && vin) {
			window.sessionStorage.setItem('vehicle', JSON.stringify(vehicleObject.data));
		}
	}, [vehicleObject.data, vehicleObject.state, vin]);

	// TODO 240120202 Evaluate if toggleUserVerificationFeedback() can be called from getVehicle instead of useEffect
	useEffect(() => {
		if (
			!featureServices ||
			!vin ||
			isVerificationLayerActive ||
			!vinToken.data.loginStatusChanged ||
			!vinToken.data.isAuthenticated ||
			vinToken.data.vinToken ||
			vehicleObject.state !== DataRequestState.Available ||
			technicalDataObject.state !== DataRequestState.Available
		) {
			return;
		}

		setIsVerificationLayerActive(true);

		const vinCheckTrackingLayerCloseUseEffect = (state: any) => {
			trackingService.track('VWBasic_LayerClose_Click', {
				eventInfo: {
					eventAction: 'VWBasic_LayerClose_Click',
					pageId: window.location.href,
					pageName: 'Vin Details',
					contentId: state.tracking.contentId,
					sectionName: state.tracking.sectionName,
					brand: 'passenger',
				},
				user: {
					loginStatus: vinToken.data.isAuthenticated,
					browserResolutionHeight: window.innerHeight,
					browserResolutionWidth: window.innerWidth,
					browserResolutionOrientation: window.innerHeight >= window.innerWidth ? 'v' : 'h',
				},
				personalization: {
					personalizable: false,
				},
				configuration: {
					CarlineName: vehicleObject.data.modelCarLine,
					SalesgroupName: vehicleObject.data.modelSalesGroup,
				},
			});
		};

		function toggleUserVerificationFeedback() {
			openContentLayer<VehicleVerificationFeedbackProps>(
				layerManager,
				VehicleVerificationFeedback,
				{
					connected: !!vehicleObject.data.includesExtendedData,
					vehicleDataIncomplete,
					identifyVehicle: () => {
						if (featureServices && featureServices['fa-content-service']) {
							setVehicleVerificationHandle(
								layerManager.openContentLayerV2(
									renderVehicleVerification,
									{
										vin: vin!,
										preFilledAuthData: technicalDataObject.data.preFilledAuthData ?? { type: '??', variant: '??', version: '??' },
										language,
										authStatus: vinToken,
										setAuthStatus: setVinToken,
										baseApiUrl: featureServices['fa-content-service']!.content.baseApiUrl,
										baseAssetsUrl: featureServices['fa-content-service']!.content.baseAssetUrl,
										apiKey: featureServices['fa-content-service']!.content.apiKey,
										vehicle: vehicleObject,
										trackingService,
										isLoggedIn: vinToken.data.isAuthenticated,
										tracking: {
											contentId: 'vinDetails.vehicleVerification',
											sectionName: 'vehicleVerification',
										},
										t,
									},
									{
										closeButtonLabel: '',
										contentHeadline: '',
										userCloseable: false,
										onClose: vinCheckTrackingLayerCloseUseEffect,
									},
								),
							);
						}
					},
					t,
				},
				{
					userCloseable: false,
				},
			);
		}

		toggleUserVerificationFeedback();
	}, [
		featureServices,
		isVerificationLayerActive,
		language,
		layerManager,
		technicalDataObject.data.preFilledAuthData,
		technicalDataObject.state,
		trackingService,
		vehicleDataIncomplete,
		vehicleObject,
		vehicleObject.data.includesExtendedData,
		vehicleObject.state,
		vin,
		vinToken,
		t,
	]);

	// Check login status and get access token if logged in.
	useEffect(() => {
		if (!authService || !featureServices || !vin) {
			return;
		}

		let loadedAuthStatus: any;
		let isInSessionStorage: boolean = false;
		if (window.sessionStorage.getItem('authStatus')) {
			loadedAuthStatus = JSON.parse(window.sessionStorage.getItem('authStatus')!);
			isInSessionStorage = true;
		}

		function checkAuthenticationStatus() {
			authServiceMarket
				.isAuthenticated()
				.then((isAuthenticatedMarket) => {
					if (isAuthenticatedMarket) {
						authService.isAuthenticated().then((isAuthenticated) => {
							if (isAuthenticated) {
								setVinToken({
									data: {
										vinToken: loadedAuthStatus?.vinToken ?? '',
										isAuthenticated: true,
										// If no token is stored, the user was previously logged out and if no data is in session storage
										// the login status did not change
										loginStatusChanged: isInSessionStorage && !loadedAuthStatus?.isAuthenticated,
									},
									state: DataRequestState.Available,
								});
							} else {
								authService.login(undefined, `${window.location.href}`).finally();
							}
						});
					} else {
						setVinToken(() => ({
							// If a token is stored, the user was previously logged in
							data: { vinToken: '', isAuthenticated: false, loginStatusChanged: isInSessionStorage && loadedAuthStatus?.isAuthenticated },
							state: DataRequestState.Available,
						}));
					}
				})
				.catch(() => {
					setVinToken((prevAuthStatus) => ({ ...prevAuthStatus, ...{ state: DataRequestState.Failed } }));
				});
		}

		checkAuthenticationStatus();
	}, [authService, authServiceMarket, featureServices, vin]);

	// Save vinToken on change to session storage
	useEffect(() => {
		if (vinToken.state === DataRequestState.Available && vin) {
			window.sessionStorage.setItem('authStatus', JSON.stringify(vinToken.data));
		}
	}, [vinToken.data, vinToken.state, vin]);

	// Recalls
	useEffect(() => {
		if (!featureServices || vehicleObject.state !== DataRequestState.Available || !vehicleObject.data.includesExtendedData || token || !vin) {
			return;
		}

		setRecalls((oldRecalls) => ({ ...oldRecalls, ...{ state: DataRequestState.Requested } }));

		getRecalls(
			authService,
			`${featureServices['fa-content-service']?.content.resourceHostId}`,
			vin,
			vehicleObject.data.productBrand,
			language,
			`${featureServices['fa-content-service']?.content.baseApiUrl}`,
			`${featureServices['fa-content-service']?.content.BID}`,
			`${featureServices['fa-content-service']?.content.countryCode}`,
			`${featureServices['fa-content-service']?.content.apiKey}`,
			vinToken.data.vinToken,
		)
			.then((res) => {
				setRecalls((oldRecalls) => ({ ...oldRecalls, ...{ data: res, state: DataRequestState.Available } }));
			})
			.catch(() => {
				setRecalls((oldRecalls) => ({ ...oldRecalls, ...{ state: DataRequestState.Failed } }));
				// TODO 11112021 add forbidden
			});
	}, [
		authService,
		featureServices,
		language,
		token,
		vehicleObject.data.includesExtendedData,
		vehicleObject.data.productBrand,
		vehicleObject.state,
		vin,
		vinToken.data.vinToken,
	]);

	useEffect(() => {
		if (!featureServices || vin || !token) {
			return;
		}

		getShareVehicleData(
			`${featureServices['fa-content-service']?.content.baseApiUrl}`,
			`${featureServices['fa-content-service']?.content.apiKey}`,
			token as string,
		).then((res) => {
			if (!res) {
				navigate('/', { state: { brokenLink: true } });
				return;
			}

			setVehicleObject({ data: res.vehicle, state: DataRequestState.Available });
			setTechnicalDataObject({ data: res.technicalDetails, state: DataRequestState.Available });
			if (res.vehicle.includesExtendedData) {
				setRecalls((prevRecalls) => ({ ...prevRecalls, ...{ data: res.recalls, state: DataRequestState.Available } }));
			}
		});
	}, [featureServices, navigate, token, vin]);

	// TODO 17082022 Fix loading issue
	// AddBreadcrumb(i18next.t('vinDetails.breadcrumb'));
	AddBreadcrumb('Informacje o pojeździe');

	const gallery = [];
	if (vehicleObject.data.productBrand === 'N') {
		gallery.push({
			key: 'front-car',
			value: vehicleObject.data.media?.frontCarImage
				? vehicleObject.data.media.frontCarImage
				: `${baseUrl}/assets/vinDetails/fallback_pictures/commercial_front_transporter.png`,
		});
		gallery.push({
			key: 'back-car',
			value: vehicleObject.data.media?.backCarImage
				? vehicleObject.data.media.backCarImage
				: `${baseUrl}/assets/vinDetails/fallback_pictures/commercial_back_transporter.png`,
		});
	} else {
		gallery.push({
			key: 'front-car',
			value: vehicleObject.data.media?.frontCarImage
				? vehicleObject.data.media.frontCarImage
				: `${baseUrl}/assets/vinDetails/fallback_pictures/private_front_golf.png`,
		});
		gallery.push({
			key: 'back-car',
			value: vehicleObject.data.media?.backCarImage
				? vehicleObject.data.media.backCarImage
				: `${baseUrl}/assets/vinDetails/fallback_pictures/private_back_golf.png`,
		});
	}

	const loadingLayer = () => {
		return (
			<>
				<div className="loading-layer" />
				{!errorVinNotFound && (
					<div className="loading-layer-content">
						<div>
							<div style={{ display: 'flex', justifyContent: 'center' }}>
								<Spinner variant="x-large" />
							</div>

							<Text appearance={TokenTextAppearance.headline300} bold>
								{t('vinDetails.loading')}
							</Text>
						</div>
					</div>
				)}
			</>
		);
	};

	return (
		<>
			{vehicleObject.state !== DataRequestState.Available && loadingLayer()}
			{errorVinNotFound
				? [!isNotFoundLayerActive && <VehicleNotFoundModal t={t} toggleVisibility={() => setNotFoundLayerActive(!isNotFoundLayerActive)} />]
				: undefined}
			{vehicleDataIncomplete && vehicleDataIncompleteLayerVisible ? (
				<div className="adjust-notification-bar">
					<NotificationBar
						icon={<Info variant="default" />}
						closeButtonLabel="close"
						onClose={() => setVehicleDataIncompleteLayerVisible(!vehicleDataIncompleteLayerVisible)}
					>
						<Text>{t('vehicleDataIncomplete')}</Text>
					</NotificationBar>
				</div>
			) : undefined}

			{appConnectNotificationVisible ? (
				<div className="adjust-notification-bar-app-connect">
					<NotificationBar
						appearance="offer"
						icon={<DecorativeImage className="app-connect-icon" src={`${baseUrl}/assets/vinDetails/VW-App-Connect-Icon-D6_M.svg`} />}
						closeButtonLabel="close notification"
						onClose={() => setAppConnectNotificationVisible(!appConnectNotificationVisible)}
						aria-label="this is an important notification"
					>
						<Container
							verticalAlign="center"
							gutter={ContainerGutter.static200}
							wrap={{
								[Breakpoints.default]: 'always',
								[Breakpoints.b560]: 'never',
							}}
							shrinkContent
						>
							<Text>{t('appConnect.information')}</Text>
							<CTA
								tag="a"
								emphasis="tertiary"
								href={getAppConnectUrl()}
								target="_blank"
								onClick={() => vinCheckTrackingAppConnectButtons('banner')}
							>
								{t('appConnect.moreInformation')} <ExternalLinkIndicator />
							</CTA>
						</Container>
					</NotificationBar>
				</div>
			) : undefined}

			<div className={vehicleObject.state !== DataRequestState.Available ? 'blur' : ''}>
				<Container
					wrap="always"
					gutter={ContainerGutter.dynamic0300}
					padding={{ top: ContainerPadding.dynamic0100, bottom: ContainerPadding.dynamic0100 }}
				>
					{/* First section TODO Move to dedicated file */}
					<div className="vindetails-first-section">
						<div className="vindetails-first-section-header">
							<Text tag={TextTag.h1} appearance={TokenTextAppearance.headline500}>
								<Text tag={TextTag.span} appearance={TokenTextAppearance.headline500} bold>
									{t('vinDetails.titlePrefix')}{' '}
								</Text>

								<Text tag={TextTag.span} appearance={TokenTextAppearance.headline500} bold>
									<span translate="no">
										{vehicleObject.state !== DataRequestState.Available && !vehicleObject.data.includesExtendedData
											? 'Arteon Shooting Brake'
											: vehicleObject.data.modelCarLine}
									</span>
								</Text>

								<Text tag={TextTag.span} appearance={TokenTextAppearance.headline500}>
									{' '}
									<span translate="no">
										{vehicleObject.state !== DataRequestState.Available && !vehicleObject.data.includesExtendedData
											? 'R-Line'
											: vehicleObject.data.modelSalesGroup}
									</span>
								</Text>
							</Text>
						</div>

						<div className="vindetails-first-section-text">
							<Container gutter={ContainerGutter.dynamic0100}>
								<div>
									<div className="color-blue" translate="no">
										<Text appearance={TokenTextAppearance.headline200} color={TokenTextColor.primary}>
											{vehicleObject.data.modelYear ? vehicleObject.data.modelYear : '──'}
										</Text>
									</div>

									<div className="color-grey">
										<Text appearance={TokenTextAppearance.label150} color={TokenTextColor.secondary}>
											{t('vinDetails.modelYear')}
										</Text>
									</div>
								</div>

								<Divider isVertical />

								<div className="vin-no">
									<div className="color-blue" translate="no">
										<Text appearance={TokenTextAppearance.headline200} color={TokenTextColor.primary}>
											{vin || (vehicleObject.state === DataRequestState.Available ? vehicleObject.data.vin : 'XXXXXXXXXXXXXXXXX')}
										</Text>
									</div>

									<div className="color-grey">
										<Text appearance={TokenTextAppearance.label150} color={TokenTextColor.secondary}>
											{t('vinDetails.vehicleNumber')}
										</Text>
									</div>
								</div>
							</Container>
						</div>

						<div className="vindetails-first-section-content">
							<Layout
								appearance={{
									[Breakpoints.b1600]: [
										{ name: 'a', columns: 15 },
										{ name: '.', columns: 2 },
										{ name: 'b', columns: 6 },
										{ name: '.', columns: 1 },
									],
									[Breakpoints.b960]: [
										{ name: 'a', columns: 14 },
										{ name: '.', columns: 2 },
										{ name: 'b', columns: 6 },
										{ name: '.', columns: 2 },
									],
									[Breakpoints.b560]: [
										{ name: 'a', columns: 24 },
										{ name: '.', columns: 4 },
										{ name: 'b', columns: 16 },
										{ name: '.', columns: 4 },
									],
									[Breakpoints.default]: [
										{ name: 'a', columns: 24 },
										{ name: '.', columns: 2 },
										{ name: 'b', columns: 20 },
										{ name: '.', columns: 2 },
									],
								}}
							>
								<div className="img-slider">
									<Gallery
										id="image-slider"
										bottomElements={
											<>
												<NavigationImages>
													{gallery.map((h) => (
														<>
															<DecorativeImage
																key={`${h.key}-bottom`}
																className="gallery-nav-image"
																alt="picture"
																src={h.value}
															/>
														</>
													))}
												</NavigationImages>
											</>
										}
										aria-label="Image Slider"
									>
										{gallery.map((h) => (
											<GallerySlide key={`${h.key}-top`} ariaLabelledby={h.key}>
												<DecorativeImage alt="picture" src={h.value} />
											</GallerySlide>
										))}
									</Gallery>

									<div className="car-img-div">
										<div className="front-car-picture">
											<img
												src={`${gallery[0].value}`}
												className="car-img"
												data-testid="front-car-picture-element"
												alt="front-car-picture"
											/>
										</div>
									</div>
								</div>

								<div className="engine-details">
									<EngineDetails
										vehicle={vehicleObject}
										isShareLink={!!token}
										hasViewPrivateDataRight={!!vehicleObject.data.includesExtendedData}
										toggleVehicleVerificationModal={vinToken.data.isAuthenticated ? toggleVehicleVerification : toggleUserVerification}
										technicalDataLoaded={technicalDataObject.state}
										trackingService={trackingService}
										isLoggedIn={vinToken.data.isAuthenticated}
										vehicleDataIncomplete={vehicleDataIncomplete}
										getAppConnectUrl={getAppConnectUrl}
										vinCheckTrackingAppConnect={vinCheckTrackingAppConnectButtons}
									/>
								</div>
							</Layout>
						</div>
					</div>

					{/* Second section TODO Move to dedicated file */}
					<div className="vindetails-second-section">
						<div className="vindetails-second-section-header">
							<Text tag={TextTag.h2} appearance={TokenTextAppearance.headline400}>
								{t('efficiencyDetails.title')}
							</Text>
						</div>

						<div className="vindetails-second-section-content">
							<Layout
								appearance={{
									[Breakpoints.b1600]: [
										{ name: 'a', columns: 15 },
										{ name: '.', columns: 2 },
										{ name: 'b', columns: 6 },
										{ name: '.', columns: 1 },
									],
									[Breakpoints.b960]: [
										{ name: 'a', columns: 14 },
										{ name: '.', columns: 2 },
										{ name: 'b', columns: 6 },
										{ name: '.', columns: 2 },
									],
									[Breakpoints.b560]: [
										{ name: 'a', columns: 0 },
										{ name: '.', columns: 4 },
										{ name: 'b', columns: 16 },
										{ name: '.', columns: 4 },
									],
									[Breakpoints.default]: [
										{ name: 'a', columns: 0 },
										{ name: '.', columns: 2 },
										{ name: 'b', columns: 20 },
										{ name: '.', columns: 2 },
									],
								}}
							>
								<div className="car-img-div">
									<div className="back-car-picture">
										<img className="car-img" src={`${gallery[1].value}`} data-testid="front-car-picture-element" alt="back-car-picture" />
									</div>
								</div>

								<div className="efficiency-details">
									<EfficiencyDetails
										vehicle={vehicleObject}
										technicalData={technicalDataObject}
										isUserAuthorized={!!vehicleObject.data.includesExtendedData}
										recallRequestStatus={recalls.state}
										getConsumption={getConsumption}
										getEmissionOrRange={getEmissionOrRange}
										numberRecalls={recalls.data?.length ?? 0}
										toggleRecallsLayer={() => {
											openContentLayer<RecallLayerProps>(layerManager, RecallLayer, { recalls, t }, {} as ContentLayerV2OptionsV2);
										}}
										toggleTechnicalDetailsLayer={() => {
											setTechnicalDetailsHandle(
												openContentLayer<TechnicalDetailsProps>(
													layerManager,
													TechnicalDetails,
													{
														technicalData: technicalDataObject,
														vehicle: vehicleObject,
														trackingService,
														isLoggedIn: vinToken.data.isAuthenticated,
														onClickDisclaimer,
														footnotes,
														tracking: {
															contentId: 'vinDetails.technicalDetails',
															sectionName: 'technicalDetails',
														},
														t,
													},
													{
														userCloseable: false,
														onClose: vinCheckTrackingLayerClose,
													},
												),
											);
										}}
										trackingService={trackingService}
										isLoggedIn={vinToken.data.isAuthenticated}
										onClickDisclaimer={onClickDisclaimer}
										footnotes={footnotes}
									/>
								</div>
							</Layout>
						</div>
					</div>

					{/* TODO 17122021 Component should work without extra spinner */}
					{vehicleObject.state !== DataRequestState.Available && !vehicleObject.data.includesExtendedData ? (
						<div className="centered-spinner">
							<Spinner variant="x-large" />
						</div>
					) : (
						(!token || (token && vehicleObject.data.includesExtendedData)) && (
							<div className="additionalEquipment">
								<SpecialEquipment
									hasViewPrivateDataRight={!!vehicleObject.data.includesExtendedData}
									vehicle={vehicleObject}
									toggleVehicleVerificationModal={vinToken.data.isAuthenticated ? toggleVehicleVerification : toggleUserVerification}
									technicalDataLoaded={technicalDataObject.state}
									trackingService={trackingService}
									isLoggedIn={vinToken.data.isAuthenticated}
									onClickDisclaimer={onClickDisclaimer}
									footnotes={footnotes}
									vehicleDataIncomplete={vehicleDataIncomplete}
								/>
							</div>
						)
					)}

					{vehicleObject.state !== DataRequestState.Available && !vehicleObject.data.includesExtendedData ? (
						<div className="centered-spinner">
							<Spinner variant="x-large" />
						</div>
					) : (
						<div className="standardEquipment">
							<StandardEquipment
								accessoryListStandard={vehicleObject.data.accessoryListStandard}
								onClickDisclaimer={onClickDisclaimer}
								footnotes={footnotes}
							/>
						</div>
					)}
				</Container>
			</div>
		</>
	);
}

export default VinDetails;

interface Props {
	authService: IAuthServiceV2;
	authServiceMarket: IAuthServiceV2;
}
